/**
 * Unified admin search input.
 *
 * One debounced query against /v2/admin/search → grouped Organizations + People
 * results in a popover under the input. UUID / `auth0|<id>` patterns get a
 * "Jump to detail" chip above the results; everything else just shows matches.
 *
 * Designed to live in AppHeader so admins can re-search from anywhere without
 * navigating back to a search page.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AdminOrganizationResponse2,
  AdminSearchProviderRow,
  AdminSearchResponse,
} from '@store/generated/admin';

import { useDebounce } from '../../../../hooks/useDebounce';
import { useAdminSearchQuery } from '../../store/api';

// Match the backend's `_MIN_QUERY_LENGTH = 3` floor — anything shorter
// can't use the pg_trgm GIN index on organizations.name and risks the
// statement_timeout fallback the index was added to close.
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const PER_GROUP_LIMIT = 8;

/** UUID v4-ish — used by admins to deep-link directly to an org. */
const UUID_RE = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
/** Auth0 user id format that providers use today. */
const AUTH0_RE = /^auth0\|[A-Za-z0-9]+$/;
/**
 * Stripe subscription/customer IDs. Unlike the UUID deep link these can't
 * navigate directly — the owning org is resolved server-side by
 * /v2/admin/search (exact match on the org's stripe columns), so the hit
 * shows up in the Organizations group below the hint.
 */
const STRIPE_ID_RE = /^(sub|cus)_\w+$/;

type DeepLink =
  | { kind: 'organization'; id: string }
  | { kind: 'provider'; id: string };

function detectDeepLink(query: string): DeepLink | null {
  const trimmed = query.trim();
  if (UUID_RE.test(trimmed)) return { kind: 'organization', id: trimmed };
  if (AUTH0_RE.test(trimmed)) return { kind: 'provider', id: trimmed };
  return null;
}

type FlatRow =
  | { kind: 'org'; org: AdminOrganizationResponse2 }
  | { kind: 'provider'; provider: AdminSearchProviderRow };

export interface UnifiedSearchProps {
  /** Render the input compactly (smaller height, no helper text). */
  dense?: boolean;
  /** Override the input width. Defaults to a header-friendly 320px. */
  width?: number | string;
  /** Fired after the user picks a result — used for cleanup (e.g. closing an overlay). */
  onResultSelected?: () => void;
}

export function UnifiedSearch({
  dense = false,
  width = 320,
  onResultSelected,
}: UnifiedSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const trimmed = debouncedQuery.trim();
  const enabled = trimmed.length >= MIN_QUERY_LENGTH;
  const deepLink = detectDeepLink(query);
  const stripeIdKind: 'subscription' | 'customer' | null = useMemo(() => {
    const q = query.trim();
    if (!STRIPE_ID_RE.test(q)) return null;
    return q.startsWith('sub_') ? 'subscription' : 'customer';
  }, [query]);

  const { data, isFetching } = useAdminSearchQuery(
    { q: trimmed, limit: PER_GROUP_LIMIT },
    { skip: !enabled },
  );

  // RTK Query drops `data` to undefined when the cache key changes, so the
  // popover would flash empty between keystrokes. Hold the most recent good
  // response and render that while a new fetch is in flight — the same
  // "stale while revalidate" trick React Query has built in.
  const [stickyData, setStickyData] = useState<AdminSearchResponse | undefined>();
  useEffect(() => {
    if (data) setStickyData(data);
  }, [data]);
  // Reset when the user clears or shortens the input so we don't keep
  // showing matches that no longer correspond to the typed query.
  useEffect(() => {
    if (!enabled) setStickyData(undefined);
  }, [enabled]);

  const display = data ?? stickyData;
  const isInitialFetch = isFetching && !display;

  const orgs = display?.organizations?.items ?? [];
  const orgsTotal = display?.organizations?.total ?? orgs.length;
  const providers = display?.providers?.items ?? [];
  const providersTotal = display?.providers?.total ?? providers.length;

  const rows: FlatRow[] = useMemo(
    () => [
      ...orgs.map(org => ({ kind: 'org' as const, org })),
      ...providers.map(provider => ({ kind: 'provider' as const, provider })),
    ],
    [orgs, providers],
  );

  // Reset highlight whenever the result set shifts so we don't keep a stale
  // index pointing at a row that no longer exists.
  useEffect(() => {
    setHighlight(0);
  }, [rows.length]);

  const close = useCallback(() => {
    setOpen(false);
    setHighlight(0);
  }, []);

  const finishSelection = useCallback(() => {
    setQuery('');
    close();
    inputRef.current?.blur();
    onResultSelected?.();
  }, [close, onResultSelected]);

  const selectRow = useCallback(
    (row: FlatRow) => {
      if (row.kind === 'org') {
        navigate(`/admin-v2/organization/${row.org.organization_id}`);
      } else {
        navigate(
          `/admin-v2/provider/${encodeURIComponent(row.provider.provider_id)}`,
        );
      }
      finishSelection();
    },
    [finishSelection, navigate],
  );

  const selectDeepLink = useCallback(
    (link: DeepLink) => {
      if (link.kind === 'organization') {
        navigate(`/admin-v2/organization/${link.id}`);
      } else {
        navigate(`/admin-v2/provider/${encodeURIComponent(link.id)}`);
      }
      finishSelection();
    },
    [finishSelection, navigate],
  );

  // Global "/" focuses the search input — but only when the user is not
  // already typing somewhere else.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (query.length) {
        setQuery('');
      } else {
        close();
        inputRef.current?.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rows.length) setHighlight(h => (h + 1) % rows.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rows.length)
        setHighlight(h => (h - 1 + rows.length) % rows.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (rows.length && rows[highlight]) {
        selectRow(rows[highlight]);
      } else if (deepLink) {
        selectDeepLink(deepLink);
      }
    }
  };

  const showPopper =
    open &&
    (deepLink ||
      enabled ||
      (query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH));

  const overflowOrgs = Math.max(0, orgsTotal - orgs.length);
  const overflowProviders = Math.max(0, providersTotal - providers.length);

  return (
    <ClickAwayListener onClickAway={close}>
      <Box sx={{ width, position: 'relative' }} ref={anchorRef}>
        <TextField
          inputRef={inputRef}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search providers, organizations, Stripe IDs…"
          fullWidth
          size={dense ? 'small' : 'medium'}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {isFetching ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <SearchIcon fontSize="small" />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />

        <Popper
          open={!!showPopper}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
          style={{ zIndex: 1300, width: anchorRef.current?.clientWidth ?? width }}
        >
          <Paper elevation={4} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {deepLink && (
              <Box>
                <DeepLinkRow link={deepLink} onClick={() => selectDeepLink(deepLink)} />
                <Divider />
              </Box>
            )}

            {stripeIdKind && (
              <Box>
                <StripeIdHintRow kind={stripeIdKind} />
                <Divider />
              </Box>
            )}

            {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Keep typing — at least {MIN_QUERY_LENGTH} characters.
                </Typography>
              </Box>
            )}

            {enabled && isInitialFetch && (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} thickness={5} />
                <Typography variant="body2" color="text.secondary">
                  Searching…
                </Typography>
              </Box>
            )}

            {enabled && !isInitialFetch && (
              <>
                <Section
                  title="Organizations"
                  icon={<BusinessIcon fontSize="small" />}
                  overflow={overflowOrgs}
                >
                  {orgs.length === 0 ? (
                    <EmptyRow label="No organizations match." />
                  ) : (
                    orgs.map((org, idx) => {
                      const flatIndex = idx;
                      return (
                        <ResultRow
                          key={org.organization_id}
                          highlighted={highlight === flatIndex}
                          onMouseEnter={() => setHighlight(flatIndex)}
                          onMouseDown={e => {
                            // Use onMouseDown so the input's onBlur doesn't
                            // close the popper before the click registers.
                            e.preventDefault();
                            selectRow({ kind: 'org', org });
                          }}
                          icon={<BusinessIcon fontSize="small" />}
                          primary={org.name}
                          secondary={
                            stripeIdKind
                              ? (stripeIdKind === 'subscription'
                                  ? org.stripe_subscription_id
                                  : org.stripe_customer_id) ?? ''
                              : `${org.member_count ?? 0} member${org.member_count === 1 ? '' : 's'}`
                          }
                        />
                      );
                    })
                  )}
                </Section>

                <Divider />

                <Section
                  title="People"
                  icon={<PersonIcon fontSize="small" />}
                  overflow={overflowProviders}
                >
                  {providers.length === 0 ? (
                    <EmptyRow label="No people match." />
                  ) : (
                    providers.map((provider, idx) => {
                      const flatIndex = orgs.length + idx;
                      return (
                        <ResultRow
                          key={provider.provider_id}
                          highlighted={highlight === flatIndex}
                          onMouseEnter={() => setHighlight(flatIndex)}
                          onMouseDown={e => {
                            e.preventDefault();
                            selectRow({ kind: 'provider', provider });
                          }}
                          icon={<PersonIcon fontSize="small" />}
                          primary={provider.name || 'Unknown Provider'}
                          secondary={provider.email || ''}
                        />
                      );
                    })
                  )}
                </Section>
              </>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  overflow: number;
  children: React.ReactNode;
}

function Section({ title, icon, overflow, children }: SectionProps) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          py: 0.5,
        }}
      >
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        {overflow > 0 && (
          <Typography variant="caption" color="text.disabled">
            + {overflow} more — refine query
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

interface ResultRowProps {
  highlighted: boolean;
  onMouseEnter: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  icon: React.ReactNode;
  primary: string;
  secondary?: string;
}

function ResultRow({
  highlighted,
  onMouseEnter,
  onMouseDown,
  icon,
  primary,
  secondary,
}: ResultRowProps) {
  return (
    <Box
      role="option"
      aria-selected={highlighted}
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        bgcolor: highlighted ? 'action.hover' : 'transparent',
      }}
    >
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {secondary}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function StripeIdHintRow({ kind }: { kind: 'subscription' | 'customer' }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: 'info.lighter',
      }}
    >
      <SearchIcon fontSize="small" color="info" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Looks like a Stripe {kind} ID
        </Typography>
        <Typography variant="body2" noWrap>
          Matching organization appears below (exact match)
        </Typography>
      </Box>
    </Box>
  );
}

function EmptyRow({ label }: { label: string }) {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="body2" color="text.disabled">
        {label}
      </Typography>
    </Box>
  );
}

interface DeepLinkRowProps {
  link: DeepLink;
  onClick: () => void;
}

function DeepLinkRow({ link, onClick }: DeepLinkRowProps) {
  const label =
    link.kind === 'organization'
      ? 'Looks like an organization ID'
      : 'Looks like a provider ID';
  const action =
    link.kind === 'organization'
      ? 'Jump to organization →'
      : 'Jump to provider →';
  return (
    <Box
      onMouseDown={e => {
        e.preventDefault();
        onClick();
      }}
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        bgcolor: 'info.lighter',
        '&:hover': { bgcolor: 'info.light' },
      }}
    >
      <ArrowForwardIcon fontSize="small" color="info" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" noWrap>
          {action}
        </Typography>
      </Box>
    </Box>
  );
}
